/**
 * Socket Cache Sync Service
 * 
 * Listens to socket events and invalidates cache based on server-driven changes.
 * Handles world state changes, player updates, and entity synchronization.
 */

import { Socket } from 'socket.io-client';
import CacheInvalidation from '../utils/CacheInvalidation';

export interface SocketSyncConfig {
  socket: Socket;
  enableWorldSync?: boolean;
  enablePlayerSync?: boolean;
  enableEntitySync?: boolean;
  enableMarketSync?: boolean;
}

export interface CacheSyncStats {
  eventsReceived: number;
  invalidationsTriggered: number;
  lastEventTime: number;
  eventsByType: Record<string, number>;
}

export class SocketCacheSync {
  private static instance: SocketCacheSync;
  private socket: Socket | null = null;
  private isInitialized = false;
  private stats: CacheSyncStats = {
    eventsReceived: 0,
    invalidationsTriggered: 0,
    lastEventTime: 0,
    eventsByType: {},
  };

  private constructor() {}

  public static getInstance(): SocketCacheSync {
    if (!SocketCacheSync.instance) {
      SocketCacheSync.instance = new SocketCacheSync();
    }
    return SocketCacheSync.instance;
  }

  /**
   * Initialize socket cache sync with existing socket connection
   */
  public initialize(config: SocketSyncConfig): void {
    if (this.isInitialized) {
      console.warn('[WARNING] SocketCacheSync already initialized');
      return;
    }

    this.socket = config.socket;
    this.isInitialized = true;

    console.log('[SOCKET] SocketCacheSync initialized');

    // Register event listeners based on config
    if (config.enableWorldSync !== false) {
      this.registerWorldSyncEvents();
    }

    if (config.enablePlayerSync !== false) {
      this.registerPlayerSyncEvents();
    }

    if (config.enableEntitySync !== false) {
      this.registerEntitySyncEvents();
    }

    if (config.enableMarketSync !== false) {
      this.registerMarketSyncEvents();
    }
  }

  /**
   * Register world state change events
   */
  private registerWorldSyncEvents(): void {
    if (!this.socket) return;

    // Chunk regeneration
    this.socket.on('world:chunkRegenerated', (data: { chunkId: string; seed?: number }) => {
      this.recordEvent('world:chunkRegenerated');
      console.log(`[CHUNK] Chunk regenerated: ${data.chunkId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });

    // World reset
    this.socket.on('world:reset', (data: { seed?: number }) => {
      this.recordEvent('world:reset');
      console.log('[WORLD] World reset triggered');
      CacheInvalidation.invalidateAll({ type: 'immediate' });
    });

    // Island state changed
    this.socket.on('world:islandChanged', (data: { islandId: string; chunkId: string }) => {
      this.recordEvent('world:islandChanged');
      console.log(`[ISLAND] Island changed: ${data.islandId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });

    // Territory claimed/unclaimed
    this.socket.on('world:territoryChanged', (data: { islandId: string; chunkId: string }) => {
      this.recordEvent('world:territoryChanged');
      console.log(`[TERRITORY] Territory changed: ${data.islandId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });

    // NPC spawned/despawned
    this.socket.on('world:npcStateChanged', (data: { npcId: string; chunkId: string; action: string }) => {
      this.recordEvent('world:npcStateChanged');
      console.log(`[NPC] NPC state changed: ${data.npcId} (${data.action})`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });

    // Fishing spot state changed
    this.socket.on('world:fishingSpotChanged', (data: { spotId: string; chunkId: string }) => {
      this.recordEvent('world:fishingSpotChanged');
      console.log(`[FISHING] Fishing spot changed: ${data.spotId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });
  }

  /**
   * Register player data sync events
   */
  private registerPlayerSyncEvents(): void {
    if (!this.socket) return;

    // Player inventory changed
    this.socket.on('player:inventoryChanged', (data: { characterId: string }) => {
      this.recordEvent('player:inventoryChanged');
      console.log(`📦 Inventory changed: ${data.characterId}`);
      CacheInvalidation.invalidatePlayerData(data.characterId, { type: 'immediate' });
    });

    // Player stats changed
    this.socket.on('player:statsChanged', (data: { characterId: string }) => {
      this.recordEvent('player:statsChanged');
      console.log(`📊 Stats changed: ${data.characterId}`);
      CacheInvalidation.invalidatePlayerData(data.characterId, { type: 'immediate' });
    });

    // Player equipment changed
    this.socket.on('player:equipmentChanged', (data: { characterId: string }) => {
      this.recordEvent('player:equipmentChanged');
      console.log(`⚔️ Equipment changed: ${data.characterId}`);
      CacheInvalidation.invalidatePlayerData(data.characterId, { type: 'immediate' });
    });

    // Player position/location changed
    this.socket.on('player:locationChanged', (data: { characterId: string; x: number; y: number }) => {
      this.recordEvent('player:locationChanged');
      console.log(`📍 Location changed: ${data.characterId}`);
      // Don't invalidate player data for location changes, just update position
    });
  }

  /**
   * Register entity synchronization events
   */
  private registerEntitySyncEvents(): void {
    if (!this.socket) return;

    // Entity position updated
    this.socket.on('entity:positionUpdated', (data: { entityId: string; x: number; y: number }) => {
      this.recordEvent('entity:positionUpdated');
      // Position updates are frequent, don't invalidate cache
    });

    // Entity state changed
    this.socket.on('entity:stateChanged', (data: { entityId: string; state: string; chunkId: string }) => {
      this.recordEvent('entity:stateChanged');
      console.log(`🎭 Entity state changed: ${data.entityId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });

    // Entity spawned
    this.socket.on('entity:spawned', (data: { entityId: string; type: string; chunkId: string }) => {
      this.recordEvent('entity:spawned');
      console.log(`✨ Entity spawned: ${data.entityId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });

    // Entity despawned
    this.socket.on('entity:despawned', (data: { entityId: string; chunkId: string }) => {
      this.recordEvent('entity:despawned');
      console.log(`💨 Entity despawned: ${data.entityId}`);
      CacheInvalidation.invalidateChunk(data.chunkId, { type: 'immediate' });
    });
  }

  /**
   * Register market data sync events
   */
  private registerMarketSyncEvents(): void {
    if (!this.socket) return;

    // Market prices updated
    this.socket.on('market:pricesUpdated', (data: { timestamp: number }) => {
      this.recordEvent('market:pricesUpdated');
      console.log('💰 Market prices updated');
      CacheInvalidation.invalidateMarketData('default', { type: 'immediate' });
    });

    // Shop inventory changed
    this.socket.on('market:inventoryChanged', (data: { shopId: string }) => {
      this.recordEvent('market:inventoryChanged');
      console.log(`🏪 Shop inventory changed: ${data.shopId}`);
      CacheInvalidation.invalidateMarketData(data.shopId, { type: 'immediate' });
    });
  }

  /**
   * Record event for statistics
   */
  private recordEvent(eventType: string): void {
    this.stats.eventsReceived++;
    this.stats.lastEventTime = Date.now();
    this.stats.invalidationsTriggered++;
    this.stats.eventsByType[eventType] = (this.stats.eventsByType[eventType] || 0) + 1;
  }

  /**
   * Get synchronization statistics
   */
  public getStats(): CacheSyncStats {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  public resetStats(): void {
    this.stats = {
      eventsReceived: 0,
      invalidationsTriggered: 0,
      lastEventTime: 0,
      eventsByType: {},
    };
  }

  /**
   * Disconnect and cleanup
   */
  public disconnect(): void {
    if (!this.socket) return;

    // Remove all listeners
    this.socket.off('world:chunkRegenerated');
    this.socket.off('world:reset');
    this.socket.off('world:islandChanged');
    this.socket.off('world:territoryChanged');
    this.socket.off('world:npcStateChanged');
    this.socket.off('world:fishingSpotChanged');
    this.socket.off('player:inventoryChanged');
    this.socket.off('player:statsChanged');
    this.socket.off('player:equipmentChanged');
    this.socket.off('player:locationChanged');
    this.socket.off('entity:positionUpdated');
    this.socket.off('entity:stateChanged');
    this.socket.off('entity:spawned');
    this.socket.off('entity:despawned');
    this.socket.off('market:pricesUpdated');
    this.socket.off('market:inventoryChanged');

    this.socket = null;
    this.isInitialized = false;

    console.log('🔌 SocketCacheSync disconnected');
  }
}

export default SocketCacheSync;
